// /var/www/aesirerp_lp/frontend/src/components/hero/HeroSection.tsx
'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { 
  Sparkles, ArrowRight, MessageSquare, ShoppingCart, 
  DollarSign, Store, Users, Zap, Warehouse, Wrench 
} from 'lucide-react';
import styles from './HeroSection.module.css';
import { useLanguage } from '@/contexts/LanguageContext';

export function HeroSection() {
  const { t } = useLanguage();
  
  const fullText1 = t('hero.title1') as string;
  const fullText2 = t('hero.title2') as string;
  
  const [typedTitle1, setTypedTitle1] = useState("");
  const [typedTitle2, setTypedTitle2] = useState("");
  const isFirstLoad = useRef(true);

  useEffect(() => {
    setTypedTitle1("");
    setTypedTitle2("");
    let index1 = 0; let index2 = 0; const typingSpeed = 30; 
    let active = true;
    
    const typeWriter = () => {
      if (!active) return;
      if (index1 < fullText1.length) {
        setTypedTitle1(fullText1.substring(0, index1 + 1));
        index1++; setTimeout(typeWriter, typingSpeed);
      } else if (index2 < fullText2.length) {
        setTypedTitle2(fullText2.substring(0, index2 + 1));
        index2++; setTimeout(typeWriter, typingSpeed);
      }
    };
    
    // Na primeira vez (F5 na página), aguarda a tela branca sumir (3.4s).
    // Se o usuário trocar de idioma depois pelo menu, redigita instantaneamente.
    const delay = isFirstLoad.current ? 3400 : 0;
    const timeout = setTimeout(typeWriter, delay);
    
    isFirstLoad.current = false;

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [fullText1, fullText2]);

  return (
    <section className={styles.hero}>
      <div className={`${styles.glowOrb} ${styles.orbOrange}`}></div>
      <div className={`${styles.glowOrb} ${styles.orbGold}`}></div>
      <div className={styles.heroGrid}></div>

      <div className={styles.container}>
        <div className={styles.textContent}>
          <div className={styles.heroBadge}>
            <span>{t('hero.badge')}</span><Sparkles size={14} color="#ea580c"/>
            {t('hero.badgeText')}
          </div>
          <h1 className={styles.heroTitle}>
            {typedTitle1} <br />
            <span className={styles.textGradient}>{typedTitle2}</span><span className={styles.cursor}></span>
          </h1>
          <p className={styles.heroDescription}>{t('hero.desc')}</p>
          <div className={styles.heroActions}>
            <a href="#planos" className={styles.btnPrimary}>{t('hero.cta1')} <ArrowRight size={18} /></a>
            <a href="https://api.whatsapp.com/send/?phone=5511983493522" target="_blank" rel="noopener noreferrer" className={styles.btnSecondary}>{t('hero.cta2')}</a>
          </div>
          <div className={styles.trustBadges}>
            <p>{t('hero.trust')}</p>
            <div className={styles.badgesRow}>
              <Image src="/assets/images/googlepartner.png" alt="Google Partner" width={140} height={70} style={{ objectFit: 'contain', opacity: 0.8 }} priority/>
              <div className={styles.separator}></div>
              <Image src="/assets/images/metapartner2.png" alt="Meta Business Partner" width={130} height={35} style={{ objectFit: 'contain', opacity: 0.8 }} priority/>
            </div>
          </div>
        </div>

        <div className={styles.visualContent}>
          <div className={styles.radialContainer}>
            <svg className={styles.connectingLines} viewBox="0 0 500 500">
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
              <line x1="250" y1="0" x2="250" y2="250" className={styles.line} />
              <line x1="425" y1="75" x2="250" y2="250" className={styles.line} />
              <line x1="500" y1="250" x2="250" y2="250" className={styles.line} />
              <line x1="425" y1="425" x2="250" y2="250" className={styles.line} />
              <line x1="250" y1="500" x2="250" y2="250" className={styles.line} />
              <line x1="75" y1="425" x2="250" y2="250" className={styles.line} />
              <line x1="0" y1="250" x2="250" y2="250" className={styles.line} />
              <line x1="75" y1="75" x2="250" y2="250" className={styles.line} />

              <circle r="4.5" fill="#ea580c" filter="url(#glow)"><animate attributeName="cx" values="250; 250" dur="2.5s" repeatCount="indefinite" begin="3.2s" /><animate attributeName="cy" values="0; 250" dur="2.5s" repeatCount="indefinite" begin="3.2s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.5s" repeatCount="indefinite" begin="3.2s" /></circle>
              <circle r="4.5" fill="#f59e0b" filter="url(#glow)"><animate attributeName="cx" values="425; 250" dur="3s" repeatCount="indefinite" begin="4.0s" /><animate attributeName="cy" values="75; 250" dur="3s" repeatCount="indefinite" begin="4.0s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="3s" repeatCount="indefinite" begin="4.0s" /></circle>
              <circle r="4.5" fill="#ea580c" filter="url(#glow)"><animate attributeName="cx" values="500; 250" dur="2.2s" repeatCount="indefinite" begin="3.5s" /><animate attributeName="cy" values="250; 250" dur="2.2s" repeatCount="indefinite" begin="3.5s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.2s" repeatCount="indefinite" begin="3.5s" /></circle>
              <circle r="4.5" fill="#fbbf24" filter="url(#glow)"><animate attributeName="cx" values="425; 250" dur="2.8s" repeatCount="indefinite" begin="4.7s" /><animate attributeName="cy" values="425; 250" dur="2.8s" repeatCount="indefinite" begin="4.7s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.8s" repeatCount="indefinite" begin="4.7s" /></circle>
              <circle r="4.5" fill="#ea580c" filter="url(#glow)"><animate attributeName="cx" values="250; 250" dur="3.2s" repeatCount="indefinite" begin="3.8s" /><animate attributeName="cy" values="500; 250" dur="3.2s" repeatCount="indefinite" begin="3.8s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="3.2s" repeatCount="indefinite" begin="3.8s" /></circle>
              <circle r="4.5" fill="#f59e0b" filter="url(#glow)"><animate attributeName="cx" values="75; 250" dur="2.4s" repeatCount="indefinite" begin="4.3s" /><animate attributeName="cy" values="425; 250" dur="2.4s" repeatCount="indefinite" begin="4.3s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.4s" repeatCount="indefinite" begin="4.3s" /></circle>
              <circle r="4.5" fill="#ea580c" filter="url(#glow)"><animate attributeName="cx" values="0; 250" dur="2.7s" repeatCount="indefinite" begin="3.4s" /><animate attributeName="cy" values="250; 250" dur="2.7s" repeatCount="indefinite" begin="3.4s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.7s" repeatCount="indefinite" begin="3.4s" /></circle>
              <circle r="4.5" fill="#fbbf24" filter="url(#glow)"><animate attributeName="cx" values="75; 250" dur="3.1s" repeatCount="indefinite" begin="5.0s" /><animate attributeName="cy" values="75; 250" dur="3.1s" repeatCount="indefinite" begin="5.0s" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="3.1s" repeatCount="indefinite" begin="5.0s" /></circle>
            </svg>

            <div className={styles.centerNode}>
              <Image src="/assets/images/logo.png" alt="Aesir Logo" width={52} height={52} style={{ objectFit: 'contain' }} />
              <h3>Aesir ERP</h3>
              <span>{t('hero.center')}</span>
            </div>

            <div className={`${styles.orbitNode} ${styles.node1}`}><div className={styles.iconCircle}><ShoppingCart size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n1.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n1.1')}</div></div></div>
            <div className={`${styles.orbitNode} ${styles.node2}`}><div className={styles.iconCircle}><MessageSquare size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n2.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n2.1')}</div></div></div>
            <div className={`${styles.orbitNode} ${styles.node3}`}><div className={styles.iconCircle}><Store size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n3.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n3.1')}</div></div></div>
            <div className={`${styles.orbitNode} ${styles.node4}`}><div className={styles.iconCircle}><DollarSign size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n4.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n4.1')}</div></div></div>
            <div className={`${styles.orbitNode} ${styles.node5}`}><div className={styles.iconCircle}><Users size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n5.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n5.1')}</div></div></div>
            <div className={`${styles.orbitNode} ${styles.node6}`}><div className={styles.iconCircle}><Warehouse size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n6.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n6.1')}</div></div></div>
            <div className={`${styles.orbitNode} ${styles.node7}`}><div className={styles.iconCircle}><Wrench size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n7.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n7.1')}</div></div></div>
            <div className={`${styles.orbitNode} ${styles.node8}`}><div className={styles.iconCircle}><Zap size={24} /></div><div className={styles.nodeInfo}><div className={styles.nodeTitle}>{t('hero.nodes.n8.0')}</div><div className={styles.nodeSub}>{t('hero.nodes.n8.1')}</div></div></div>
          </div>
        </div>
      </div>
    </section>
  );
}